const reasons = [
  {
    icon: 'ri-medal-2-line',
    title: 'Master-Level Craftsmanship',
    desc: 'Every plank is precision-measured, hand-cut, and fitted with exacting care. Our craftsmen treat every floor as if it were their own home.',
    stat: '500+',
    statLabel: 'Projects',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Premium Materials Only',
    desc: 'We source exclusively from top-grade suppliers — superior grain quality, stable kiln drying, and finishes that last decades.',
    stat: '20+',
    statLabel: 'Wood Species',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Honest, Transparent Pricing',
    desc: 'Itemized written quotes before a single board is laid. No change-orders, no hidden fees, no surprises at completion.',
    stat: '100%',
    statLabel: 'Transparent',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Dust-Minimized Worksites',
    desc: 'We use professional dust containment systems and protect all furniture. Your home is left cleaner than when we arrived.',
    stat: '0',
    statLabel: 'Hidden Fees',
  },
  {
    icon: 'ri-community-line',
    title: 'Deep Local Reputation',
    desc: 'Trusted by Maryland homeowners for over 15 years. Our reputation is built on referrals and repeat business — not advertising.',
    stat: '15+',
    statLabel: 'Years Local',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Post-Job Support',
    desc: 'We stand behind every project. After completion, we\'re still just a call away for maintenance guidance, questions, or touch-ups.',
    stat: '24hr',
    statLabel: 'Response',
  },
];

interface WhyChooseUsProps {
  onEstimateClick: () => void;
}

export default function WhyChooseUs({ onEstimateClick }: WhyChooseUsProps) {
  return (
    <section className="bg-sand py-28 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-5 gap-14 items-start">
          {/* Left: Sticky content */}
          <div className="lg:col-span-2 lg:sticky lg:top-28">
            <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Why Choose Us</span>
            <h2 className="font-serif text-4xl md:text-5xl text-walnut font-bold mt-3 mb-6 leading-tight">
              The Premier Difference
            </h2>
            <p className="font-sans text-base text-charcoal/65 leading-relaxed mb-8">
              Maryland homeowners have trusted Premier Hardwood Floors for over 15 years. Our reputation isn&apos;t built on marketing — it&apos;s built on results that speak for themselves.
            </p>

            {/* Image */}
            <div className="rounded-2xl overflow-hidden relative mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/2b3dd10e92fab041fdce93475d94d8b6.jpg"
                alt="Premier Hardwood Floors craftsman precision work"
                className="w-full h-72 object-cover object-top"
              />
              <div className="absolute bottom-4 right-4 bg-walnut text-white rounded-xl px-5 py-3">
                <div className="font-serif text-2xl text-gold font-bold">15+</div>
                <div className="font-sans text-xs text-white/70">Years of Excellence</div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onEstimateClick}
                className="flex-1 px-6 py-3.5 bg-walnut text-white rounded-xl font-sans text-sm font-bold hover:bg-walnut-dark transition-colors whitespace-nowrap text-center"
              >
                Get Free Estimate
              </button>
              <a
                href="tel:+14105550192"
                className="flex-1 px-6 py-3.5 border-2 border-walnut/25 text-walnut rounded-xl font-sans text-sm font-semibold hover:bg-white transition-colors whitespace-nowrap text-center"
              >
                Call Us
              </a>
            </div>
          </div>

          {/* Right: Reasons Grid */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {reasons.map((r) => (
              <div key={r.title} className="bg-white rounded-2xl p-7 border border-sand-dark hover:border-oak/30 transition-colors group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl group-hover:bg-gold/20 transition-colors">
                    <i className={`${r.icon} text-gold text-xl`} />
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-xl text-walnut font-bold">{r.stat}</div>
                    <div className="font-sans text-xs text-charcoal/50">{r.statLabel}</div>
                  </div>
                </div>
                <h3 className="font-sans text-base font-bold text-walnut mb-2">{r.title}</h3>
                <p className="font-sans text-sm text-charcoal/60 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
